import React, { useState } from 'react';
import { AnimatePresence } from 'framer-motion';
import { Team } from '@/lib/types/common/Team';
import { Player } from '@/lib/types/common/Player';
import { GameStep } from '@/lib/types/common/GameStep';
import { GameMode } from '@/lib/types/common/GameMode';
import LobbyPanel from './LobbyPanel';
import { PlayerRow } from './shared';
import BarFFXIV from '@/app/components/BarFFXIV';
import { useGame } from '@/app/providers/GameContextProvider';
import TeamSelector from './TeamSelector';

interface TeamPanelProps {
    team: Team;
    players: { sessionId: string; player: Player; index: number }[];
    isPlayerTeam: boolean;
    onJoin: () => void;
    canJoin: boolean;
    onAvatarClick: () => void;
    currentPlayerSessionId: string;
    gameStep: GameStep;
    gameMode: GameMode;
    gameMasterId: string;
    currentRound: number;
    currentHazards: string[];
    isInLobby: boolean;
}

const TeamPanel: React.FC<TeamPanelProps> = ({
    team,
    players,
    isPlayerTeam,
    onJoin,
    canJoin,
    onAvatarClick,
    currentPlayerSessionId,
    gameStep,
    gameMode,
    gameMasterId,
    currentRound,
    currentHazards,
    isInLobby,
}) => {
    const { gameState, room } = useGame();
    const [isTeamSelectorOpen, setIsTeamSelectorOpen] = useState(false);
    const effectiveGameStep = gameStep === GameStep.LOBBY || isInLobby ? GameStep.LOBBY : gameStep;

    // HP bar is only shown in Fight mode or Custom mode with Fight base
    const isFightMode = gameMode === GameMode.FIGHT ||
        (gameMode === GameMode.CUSTOM && gameState?.gameSettings?.baseMode === GameMode.FIGHT);
    // eslint-disable-next-line @typescript-eslint/no-non-null-asserted-optional-chain
    const percent = team.currentHp / gameState?.gameSettings.teamMaxHp! * 100;

    const handleEditTeam = () => {
        if (effectiveGameStep === GameStep.LOBBY) {
            setIsTeamSelectorOpen(true);
        }
    };

    const handleTeamUpdate = (color: string, name: string) => {
        if (room) {
            room.send('updateTeam', { teamId: team.id, color, name });
        }
    };

    return (
        <>
            <LobbyPanel
                name={team.name}
                color={team.color}
                disabled={isFightMode && team.currentHp === 0}
                className={`!grow !w-full rounded`}
                onJoin={canJoin && !isPlayerTeam && effectiveGameStep === GameStep.LOBBY ? onJoin : undefined}
                onEditTeam={isPlayerTeam && effectiveGameStep === GameStep.LOBBY ? handleEditTeam : undefined}
            >
                {isFightMode && effectiveGameStep !== GameStep.LOBBY && (
                    <BarFFXIV
                        width="w-full"
                        height="h-2.5"
                        percent={percent}
                        number={team.currentHp}
                        className='mb-3'
                    />
                )}
                <div className="flex flex-col gap-2 md:gap-4 min-w-40">
                    <AnimatePresence mode="popLayout">
                        {players.map(({ sessionId, player }) => (
                            <PlayerRow
                                key={sessionId}
                                sessionId={sessionId}
                                player={player}
                                isCurrentPlayer={sessionId === currentPlayerSessionId}
                                isInLobby={isInLobby}
                                gameStep={gameStep}
                                gameMasterId={gameMasterId}
                                currentRound={currentRound}
                                currentHazards={currentHazards}
                                onAvatarClick={onAvatarClick}
                            />
                        ))}
                    </AnimatePresence>
                </div>
            </LobbyPanel>
            <TeamSelector
                isOpen={isTeamSelectorOpen}
                currentColor={team.color}
                currentName={team.name}
                onSelect={handleTeamUpdate}
                onClose={() => setIsTeamSelectorOpen(false)}
            />
        </>
    );
};

export default TeamPanel;
